import {
  BaseBoxShapeUtil,
  resizeBox,
  type ResizeInfo,
  HTMLContainer,
  RichTextLabel,
  renderHtmlFromRichText,
  renderPlaintextFromRichText,
} from "./canvas";
import { useEditor } from "./canvas";
import { useValue } from "@tldraw/state-react";
import { toRichText } from "@tldraw/tlschema";
import {
  documentProps,
  tableProps,
  type BoardDocument,
  type BoardTable,
} from "../shared/schema";
import {
  checkedRows,
  MAX_CONTENT_BYTES,
  contentBytes,
} from "../shared/file-content";
import {
  cellStyleAt,
  displayTableRows,
  normalizeCellRange,
  tableCellStyles,
  tableColumnWidths,
  tableObjectHeight,
  tableRowHeights,
  type CellStyle,
} from "../shared/table-tools";
import {
  getTableInteraction,
  selectTableCell,
  subscribeTableInteraction,
} from "./table-interaction";
import { notify } from "./api";
import { documentTypography } from "./document-format";
import { useSyncExternalStore, type CSSProperties } from "react";
import { nativeDocument } from "../shared/document-session";
import {
  nativeDocumentHtml,
  nativeDocumentPlaintext,
} from "./document-richtext";
import { CanvasDocument } from "./CanvasDocument";
const safeColor = (value: unknown, fallback: string) =>
  /^#[0-9a-f]{6}$/i.test(String(value)) ? String(value) : fallback;
const tableStyle = (shape: BoardTable) =>
  ({
    "--cell-font": `${Math.max(10, Math.min(32, Number(shape.meta.fontSize) || 14))}px`,
    "--cell-color": safeColor(shape.meta.textColor, "#25343b"),
    "--cell-background": safeColor(shape.meta.background, "#ffffff"),
    "--cell-border": safeColor(shape.meta.border, "#d6dad3"),
    "--cell-align": ["left", "center", "right"].includes(
      String(shape.meta.align),
    )
      ? String(shape.meta.align)
      : "left",
    "--cell-weight": shape.meta.bold ? "600" : "normal",
    "--cell-family": ["Arial", "Georgia", "monospace"].includes(
      String(shape.meta.fontFamily),
    )
      ? String(shape.meta.fontFamily)
      : "Arial",
    "--cell-italic": shape.meta.italic ? "italic" : "normal",
  }) as CSSProperties;

function cellStyle(
  shape: BoardTable,
  style: CellStyle,
  header: boolean,
): CSSProperties {
  const fallback = tableStyle(shape) as Record<string, string>;
  return {
    background:
      style.fill || (header ? "#eaece6" : fallback["--cell-background"]),
    color: style.textColor || fallback["--cell-color"],
    borderColor: style.border || fallback["--cell-border"],
    fontSize: style.fontSize ? `${style.fontSize}px` : fallback["--cell-font"],
    textAlign: (style.align ||
      fallback["--cell-align"]) as CSSProperties["textAlign"],
    fontWeight: (style.bold ?? header) ? 600 : fallback["--cell-weight"],
    fontFamily: style.fontFamily || fallback["--cell-family"],
    fontStyle: style.italic ? "italic" : fallback["--cell-italic"],
  };
}
export function documentHeightFor(
  shape: Pick<BoardDocument, "props"> & Partial<Pick<BoardDocument, "meta">>,
  editor: import("./canvas").Editor,
) {
  const isNative = shape.meta?.nativeDocument === true;
  const measured = editor.textMeasure.measureHtml(
    isNative
      ? nativeDocumentHtml(shape.props.richText)
      : renderHtmlFromRichText(editor, shape.props.richText),
    {
      ...(isNative
        ? {
            fontFamily: "Arial",
            fontSize: shape.props.fontSize,
            lineHeight: 1.5,
          }
        : documentTypography(shape)),
      fontWeight: "normal",
      fontStyle: "normal",
      padding: "0px",
      maxWidth: Math.max(160, shape.props.w - 64),
    },
  );
  return isNative
    ? Math.min(800, Math.max(320, measured.h + 175))
    : Math.max(320, measured.h + 112);
}
export class DocumentUtil extends BaseBoxShapeUtil<BoardDocument> {
  static override type = "document" as const;
  static override props = documentProps;
  getDefaultProps() {
    return {
      w: 560,
      h: 720,
      name: "Document",
      source: "",
      richText: toRichText(""),
      fontSize: 18,
    };
  }
  override canEdit() {
    return true;
  }
  override canScroll(shape: BoardDocument) {
    return nativeDocument(shape);
  }
  override hideRotateHandle(shape: BoardDocument) {
    return nativeDocument(shape);
  }
  override getIndicatorPath(shape: BoardDocument) {
    const path = new Path2D();
    path.rect(0, 0, shape.props.w, shape.props.h);
    return path;
  }
  override onBeforeCreate(shape: BoardDocument) {
    return {
      ...shape,
      ...(nativeDocument(shape)
        ? {
            meta: {
              ...shape.meta,
              documentVersion: 0,
              documentWriter: null,
              documentWriterUntil: 0,
              documentWriterBaseVersion: 0,
            },
          }
        : {}),
      props: {
        ...shape.props,
        h: Math.min(30000, documentHeightFor(shape, this.editor)),
      },
    };
  }
  override onResize(shape: BoardDocument, info: ResizeInfo<BoardDocument>) {
    return resizeBox(shape, info, {
      minWidth: nativeDocument(shape) ? 320 : 240,
      maxWidth: 10000,
      minHeight: nativeDocument(shape) ? shape.props.h : 320,
      maxHeight: nativeDocument(shape) ? shape.props.h : 30000,
    });
  }
  override onBeforeUpdate(prev: BoardDocument, next: BoardDocument) {
    if (nativeDocument(prev) && next.parentId !== prev.parentId) return prev;
    if (contentBytes(next.props.richText) > MAX_CONTENT_BYTES) {
      notify("Document text limit reached. Split this into another document.");
      return prev;
    }
    if (
      prev.props.richText !== next.props.richText ||
      prev.props.w !== next.props.w ||
      prev.props.fontSize !== next.props.fontSize ||
      prev.meta.calqueLineHeight !== next.meta.calqueLineHeight
    ) {
      const height = documentHeightFor(next, this.editor);
      if (height > 30000) {
        notify(
          "This document would be clipped. Split it into another board document.",
        );
        return prev;
      }
      return {
        ...next,
        props: { ...next.props, h: height },
      };
    }
    return next;
  }
  component(shape: BoardDocument) {
    const selected = useValue(
      "doc selection",
      () => this.editor.getOnlySelectedShapeId() === shape.id,
      [shape.id],
    );
    if (nativeDocument(shape)) {
      const html = nativeDocumentHtml(shape.props.richText);
      const fullHeight =
        this.editor.textMeasure.measureHtml(html, {
          fontFamily: "Arial",
          fontSize: shape.props.fontSize,
          fontWeight: "normal",
          fontStyle: "normal",
          lineHeight: 1.5,
          padding: "0px",
          maxWidth: Math.max(160, shape.props.w - 64),
        }).h + 175;
      return <CanvasDocument shape={shape} continues={fullHeight > 800} />;
    }
    return (
      <HTMLContainer
        className="board-document"
        style={{ width: shape.props.w, height: shape.props.h }}
      >
        <div className="content-object-title">{shape.props.name}</div>
        <div
          style={{
            position: "absolute",
            left: 32,
            top: 64,
            width: shape.props.w - 64,
            height: shape.props.h - 96,
          }}
        >
          <RichTextLabel
            shapeId={shape.id}
            type="document"
            richText={shape.props.richText}
            fontFamily={documentTypography(shape).fontFamily}
            fontSize={documentTypography(shape).fontSize}
            lineHeight={documentTypography(shape).lineHeight}
            textAlign="start"
            verticalAlign="start"
            labelColor="#25343b"
            isSelected={selected}
            textWidth={shape.props.w - 64}
            textHeight={shape.props.h - 96}
            wrap
            showTextOutline={false}
          />
        </div>
      </HTMLContainer>
    );
  }
  override toSvg(shape: BoardDocument) {
    // Only editor-generated HTML from validated rich-text JSON enters the export.
    const html = (
      nativeDocument(shape)
        ? nativeDocumentHtml(shape.props.richText)
        : renderHtmlFromRichText(this.editor, shape.props.richText)
    ).replaceAll("<p", '<p style="margin:0"');
    if (nativeDocument(shape))
      return (
        <foreignObject width={shape.props.w} height={shape.props.h}>
          <div
            {...{ xmlns: "http://www.w3.org/1999/xhtml" }}
            className="native-document-export"
            style={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              position: "relative",
              background: "#fffefa",
              color: "#302e29",
              border: "1px solid #d7d4cc",
              boxSizing: "border-box",
              fontFamily: "Arial",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "26px 32px 17px",
                fontSize: 9,
                letterSpacing: 1.7,
                color: "#99948a",
              }}
            >
              <span />
              <span>
                {String(Number(shape.meta.order ?? 0) + 1).padStart(2, "0")}
              </span>
            </div>
            <div
              style={{
                margin: "0 32px",
                paddingBottom: 20,
                borderBottom: "1px solid #d9d6ce",
                fontFamily: "Georgia,serif",
                fontSize: 30,
                lineHeight: 1.15,
                letterSpacing: -0.7,
              }}
            >
              {shape.props.name}
            </div>
            <div
              style={{
                padding: "24px 32px",
                fontSize: 17,
                color: "#57534a",
                lineHeight: 1.65,
                maxHeight: shape.props.h - 175,
                overflow: "hidden",
              }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 32,
                right: 32,
                background: "#fffefa",
                padding: "14px 0",
                fontSize: 10,
                letterSpacing: 0.7,
                color: "#656158",
                borderTop: "1px solid #d9d6ce",
              }}
            >
              Canvas preview · open or download the full document
            </div>
          </div>
        </foreignObject>
      );
    return (
      <foreignObject width={shape.props.w} height={shape.props.h}>
        <div
          {...{ xmlns: "http://www.w3.org/1999/xhtml" }}
          style={{
            position: "relative",
            background: "white",
            height: "100%",
            boxSizing: "border-box",
            border: "1px solid #d6dad3",
            borderRadius: 6,
            overflow: "hidden",
            ...documentTypography(shape),
            color: "#25343b",
          }}
        >
          <div
            style={{
              height: 44,
              padding: "13px 16px",
              boxSizing: "border-box",
              fontSize: 13,
              fontWeight: 600,
              color: "#5d695f",
              background: "#f5f3ed",
              borderBottom: "1px solid #e4e6df",
            }}
          >
            {shape.props.name}
          </div>
          <div
            style={{
              position: "absolute",
              left: 32,
              top: 64,
              width: shape.props.w - 64,
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </foreignObject>
    );
  }
  override getText(shape: BoardDocument) {
    return nativeDocument(shape)
      ? nativeDocumentPlaintext(shape.props.richText)
      : renderPlaintextFromRichText(this.editor, shape.props.richText);
  }
}
function TableBody({
  shape,
  exporting = false,
}: {
  shape: BoardTable;
  exporting?: boolean;
}) {
  const editor = useEditor();
  const editing = useValue(
    "table editing",
    () =>
      !exporting &&
      editor.getEditingShapeId() === shape.id &&
      !editor.getIsReadonly() &&
      !shape.isLocked,
    [editor, shape.id, shape.isLocked, exporting],
  );
  const interaction = useSyncExternalStore(
      (listener) => subscribeTableInteraction(shape.id, listener),
      () => getTableInteraction(shape.id),
      () => getTableInteraction(shape.id),
    ),
    range = normalizeCellRange(
      {
        startRow: interaction.anchor.row,
        startColumn: interaction.anchor.column,
        endRow: interaction.focus.row,
        endColumn: interaction.focus.column,
      },
      shape.props.rows,
    ),
    widths = tableColumnWidths(shape.meta, shape.props.rows[0].length),
    heights = tableRowHeights(
      shape.meta,
      shape.props.rows,
      shape.props.layout === "grid",
    ),
    styles = tableCellStyles(shape.meta),
    displayed = displayTableRows(
      shape.props.rows,
      shape.props.headers,
      shape.meta,
      interaction.search,
    );
  const update = (r: number, c: number, text: string) => {
    const current = editor.getShape<BoardTable>(shape.id);
    if (!current || editor.getIsReadonly() || current.isLocked) return;
    const rows = current.props.rows.map((row) => [...row]);
    if (!rows[r] || c >= rows[r].length) return;
    rows[r][c] = text;
    try {
      editor.updateShape<BoardTable>({
        id: shape.id,
        type: "table",
        props: {
          rows: checkedRows(rows),
          ...(current.meta.autoFitRows === true
            ? {
                h: tableObjectHeight(
                  rows,
                  current.meta,
                  current.props.layout === "grid",
                ),
              }
            : {}),
        },
      });
    } catch (e) {
      notify((e as Error).message);
    }
  };
  return (
    <div
      className={`board-table ${shape.props.layout}`}
      style={{
        ...tableStyle(shape),
        width: shape.props.w,
        height: shape.props.h,
        pointerEvents: editing ? "all" : undefined,
      }}
      onPointerDown={editing ? (e) => e.stopPropagation() : undefined}
      onKeyDown={
        editing
          ? (e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                editor.setEditingShape(null);
                editor.setCurrentTool("select");
              }
            }
          : undefined
      }
    >
      <div className="content-object-title">{shape.props.name}</div>
      <table aria-label={shape.props.name}>
        <colgroup>
          {widths.map((width, column) => (
            <col key={column} style={{ width }} />
          ))}
        </colgroup>
        <tbody>
          {shape.props.headers && renderRow(shape.props.rows[0], 0, true, true)}
          {displayed.map((entry, index) =>
            entry.kind === "group" ? (
              <tr className="table-group" key={`group-${index}-${entry.label}`}>
                <th colSpan={shape.props.rows[0].length}>{entry.label}</th>
              </tr>
            ) : (
              renderRow(entry.row, entry.sourceIndex, false, entry.searchMatch)
            ),
          )}
        </tbody>
      </table>
    </div>
  );

  function renderRow(
    row: string[],
    r: number,
    header: boolean,
    searchMatch: boolean,
  ) {
    return (
      <tr
        key={r}
        className={searchMatch ? "table-search-match" : "table-search-miss"}
      >
        {row.map((cell, c) => {
          const Cell = header ? "th" : "td",
            selected =
              r >= range.startRow &&
              r <= range.endRow &&
              c >= range.startColumn &&
              c <= range.endColumn;
          return (
            <Cell
              key={c}
              data-selected={selected || undefined}
              style={{
                ...cellStyle(shape, styles[`${r}:${c}`] || {}, header),
                height: heights[r],
              }}
              onPointerDown={
                editing
                  ? (event) => selectTableCell(shape.id, r, c, event.shiftKey)
                  : undefined
              }
              onPointerEnter={
                editing
                  ? (event) => {
                      if (event.buttons === 1)
                        selectTableCell(shape.id, r, c, true);
                    }
                  : undefined
              }
            >
              {editing ? (
                <textarea
                  aria-label={`Row ${r + 1}, column ${c + 1}`}
                  value={cell}
                  maxLength={4000}
                  onFocus={() => {
                    const current = getTableInteraction(shape.id);
                    if (current.focus.row !== r || current.focus.column !== c)
                      selectTableCell(shape.id, r, c);
                    editor.markHistoryStoppingPoint("Edit cell");
                  }}
                  onKeyDown={(event) => {
                    if (!event.shiftKey) return;
                    const movement: Record<string, [number, number]> = {
                      ArrowUp: [-1, 0],
                      ArrowDown: [1, 0],
                      ArrowLeft: [0, -1],
                      ArrowRight: [0, 1],
                    };
                    const delta = movement[event.key];
                    if (!delta) return;
                    event.preventDefault();
                    const current = getTableInteraction(shape.id).focus;
                    selectTableCell(
                      shape.id,
                      Math.max(
                        0,
                        Math.min(
                          shape.props.rows.length - 1,
                          current.row + delta[0],
                        ),
                      ),
                      Math.max(
                        0,
                        Math.min(row.length - 1, current.column + delta[1]),
                      ),
                      true,
                    );
                  }}
                  onChange={(e) => update(r, c, e.target.value)}
                />
              ) : (
                <span
                  style={{
                    height: heights[r],
                  }}
                >
                  {cell}
                </span>
              )}
            </Cell>
          );
        })}
      </tr>
    );
  }
}
export class TableUtil extends BaseBoxShapeUtil<BoardTable> {
  static override type = "table" as const;
  static override props = tableProps;
  getDefaultProps() {
    return {
      w: 600,
      h: 180,
      name: "Table",
      source: "",
      rows: [
        ["Column 1", "Column 2"],
        ["", ""],
      ],
      headers: true,
      layout: "table" as const,
    };
  }
  override canEdit() {
    return true;
  }
  override getIndicatorPath(shape: BoardTable) {
    const path = new Path2D();
    path.rect(0, 0, shape.props.w, shape.props.h);
    return path;
  }
  component(shape: BoardTable) {
    return (
      <HTMLContainer>
        <TableBody shape={shape} />
      </HTMLContainer>
    );
  }
  override onResize(shape: BoardTable, info: ResizeInfo<BoardTable>) {
    return resizeBox(shape, info, {
      minWidth: 240,
      maxWidth: 10000,
      minHeight: tableObjectHeight(
        shape.props.rows,
        shape.meta,
        shape.props.layout === "grid",
      ),
      maxHeight: 30000,
    });
  }
  override toSvg(shape: BoardTable) {
    const widths = tableColumnWidths(shape.meta, shape.props.rows[0].length),
      heights = tableRowHeights(
        shape.meta,
        shape.props.rows,
        shape.props.layout === "grid",
      ),
      styles = tableCellStyles(shape.meta),
      displayed = displayTableRows(
        shape.props.rows,
        shape.props.headers,
        shape.meta,
      );
    return (
      <foreignObject width={shape.props.w} height={shape.props.h}>
        <div
          {...{ xmlns: "http://www.w3.org/1999/xhtml" }}
          style={{
            height: "100%",
            background: "white",
            border: "1px solid #d6dad3",
            borderRadius: 6,
            overflow: "hidden",
            boxSizing: "border-box",
            fontFamily: "Arial",
          }}
        >
          <div
            style={{
              height: 44,
              padding: "13px 16px",
              boxSizing: "border-box",
              fontSize: 13,
              fontWeight: 600,
              color: "#5d695f",
              background: "#f5f3ed",
              borderBottom: "1px solid #e4e6df",
            }}
          >
            {shape.props.name}
          </div>
          <table
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse:
                shape.props.layout === "grid" ? "separate" : "collapse",
              borderSpacing: shape.props.layout === "grid" ? 5 : 0,
              padding: shape.props.layout === "grid" ? 3 : 0,
            }}
          >
            <colgroup>
              {widths.map((width, column) => (
                <col key={column} style={{ width }} />
              ))}
            </colgroup>
            <tbody>
              {shape.props.headers &&
                renderExportRow(shape.props.rows[0], 0, true)}
              {displayed.map((entry, index) =>
                entry.kind === "group" ? (
                  <tr key={`group-${index}-${entry.label}`}>
                    <td
                      colSpan={shape.props.rows[0].length}
                      style={{
                        height: 28,
                        padding: "6px 10px",
                        background: "#f4f0e9",
                        color: "#5d695f",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {entry.label}
                    </td>
                  </tr>
                ) : (
                  renderExportRow(entry.row, entry.sourceIndex, false)
                ),
              )}
            </tbody>
          </table>
        </div>
      </foreignObject>
    );

    function renderExportRow(row: string[], r: number, header: boolean) {
      return (
        <tr key={r}>
          {row.map((text, c) => {
            const style = cellStyle(shape, styles[`${r}:${c}`] || {}, header);
            return (
              <td
                key={c}
                style={{
                  padding: 0,
                  verticalAlign: "top",
                  border: `1px solid ${style.borderColor}`,
                  borderRadius: shape.props.layout === "grid" ? 4 : 0,
                  background: style.background,
                  color: style.color,
                  fontSize: style.fontSize,
                  textAlign: style.textAlign,
                  fontWeight: style.fontWeight,
                  fontFamily: style.fontFamily,
                  fontStyle: style.fontStyle,
                }}
              >
                <div
                  style={{
                    height: heights[r],
                    padding: 10,
                    boxSizing: "border-box",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    overflow: "hidden",
                  }}
                >
                  {text}
                </div>
              </td>
            );
          })}
        </tr>
      );
    }
  }
  override getText(shape: BoardTable) {
    return shape.props.rows.map((r) => r.join("\t")).join("\n");
  }
}
